<template>
    <MainContent title="Configurar invitaciones de abonados">
        <template #header>
            <h5 class="pb-2">
                Opciones para configurar las invitaciones de abonados para este evento
            </h5>
        </template>
        <template #content>
            <ValidationObserver tag="form" ref="observer" @submit.prevent="submit()">
                <ContentOrLoading>
                    <b-row v-if="evento">
                        <b-col cols="12">
                            <FormInput 
                                v-model="evento.n_invitaciones_abonados" 
                                id="n_invitaciones_abonados"
                                name="número máximo de invitaciones por abonado"
                                label="Número máximo de invitaciones que puede sacar un abonado"
                                rules="required"
                                type="number"
                                min="1"
                                :quitar_pr="true"
                                :disabled="loading"
                            />
                        </b-col>
                        <b-col cols="12">
                            <ValidationProvider name="restricción de invitaciones" rules="required" v-slot="{ errors }">
                                <div class="form-group">
                                    <label>
                                        Restricción para la selección de invitaciones del abonado
                                        <small v-if="!esMovil">
                                            *En este selector podrá configurar en qué lugar el abonado podrá adquirir sus invitaciones.
                                        </small>
                                        <InfoToolTip v-else info="En este selector podrá configurar en qué lugar el abonado podrá adquirir sus invitaciones." />
                                    </label>
                                    <multiselect
                                        v-model="evento.tipo_invitaciones_abonados"
                                        label="text"
                                        track-by="value"
                                        placeholder="Selecciona una restricción para el abonado"
                                        style="z-index: 999;"
                                        :options="opcionesTipoInvitacionesAbonados"
                                        :disabled="loading"
                                        @input="resetData"
                                    />
                                    <span v-if="errors[0]" class="d-block invalid-feedback text-xs bg-danger white h7 mb-4 p-2 font-weight-bold">
                                        {{ errors[0] }}
                                    </span>
                                </div>
                            </ValidationProvider>
                        </b-col>
                        <b-col cols="12" v-if="zonas.length">
                            <hr/>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-2">
                                    Zonas disponibles
                                    <InfoToolTip info="Desde esta tabla puedes seleccionar en qué zonas, ya sea online o en taquilla, el abonado podrá adquirir invitaciones, hasta alcanzar el número máximo que configuraste anteriormente." />
                                </h6>
                                <div class="text-right mb-2">
                                    <div class="custom-control custom-checkbox d-inline-flex">
                                        <input type="checkbox" v-model="marcar_en_todos" id="marcar_en_todos" class="custom-control-input" :disabled="loading" />
                                        <label for="marcar_en_todos" class="custom-control-label pt-1">
                                            MARCAR EN TODOS
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="responsive mb-2">
                                <SearchComponent v-model="search" :disabled="loading" />
                            </div>
                        </b-col>
                        <b-col cols="12" v-if="evento.tipo_invitaciones_abonados?.value === 1" style="height: calc(100svh - 525px); overflow-y: auto;">
                            <div v-if="!loading_zonas">
                                <template v-if="zonas.length">
                                    <div v-for="(zona, index) in zonas" :key="index" class="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
                                        <span>{{ zona.name }}</span>
                                        <div class="d-flex align-items-center">
                                            <div class="custom-control custom-checkbox mr-2 mr-md-3">
                                                <input
                                                    v-model="zona.invitaciones_abonado.taquilla"
                                                    type="checkbox"
                                                    class="custom-control-input"
                                                    :id="'taquilla-' + zona.id"
                                                    :disabled="loading"
                                                    @input="syncChange($event, 'taquilla', zona)"
                                                />
                                                <label :for="'taquilla-' + zona.id" class="custom-control-label pt-1">
                                                    Taquilla
                                                </label>
                                            </div>
                                            <div class="custom-control custom-checkbox">
                                                <input
                                                    v-model="zona.invitaciones_abonado.online"
                                                    type="checkbox"
                                                    class="custom-control-input"
                                                    :id="'online-' + zona.id"
                                                    :disabled="loading"
                                                    @input="syncChange($event, 'online', zona)"
                                                />
                                                <label :for="'online-' + zona.id" class="custom-control-label pt-1">
                                                    Online
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="mt-3">
                                    <h4>No hay ninguna zona disponible</h4>  
                                </div>
                            </div>
                            <div v-else class="text-center my-5">
                                <div class="spinner-border text-primary" role="status"></div>
                            </div>
                        </b-col>
                        <b-col v-if="errors.length" cols="12" :class="{ 'mt-2': evento.tipo_invitaciones_abonados.value === 1 }">
                            <div class="alert alert-danger">
                                <div v-for="(error, index) in errors" :key="index">
                                    {{ ++index }}. {{ error }}
                                </div>
                            </div>
                        </b-col>
                        <b-col v-if="puede_guardar" cols="12" class="text-right" :class="{ 'mt-3': evento.tipo_invitaciones_abonados.value === 1 && !errors.length }">
                            <b-button variant="primary" class="w-responsive" type="submit" :disabled="loading">
                                Guardar
                            </b-button>
                        </b-col>
                    </b-row>
                </ContentOrLoading>
            </ValidationObserver>
        </template>
    </MainContent>
</template>

<script>
    import InfoToolTip from '@/components/common/InfoToolTip';
    import MainContent from "@/components/backend/components/MainContent";
    import SearchComponent from "@/components/backend/components/SearchComponent";

    export default {
        props: {
            evento_id: Number
        },
        data() {
            return {
                zonas: [],
                search: '',
                errors: [],
                evento: null,
                loading: false,
                loading_zonas: false,
                marcar_en_todos: false,
                opcionesTipoInvitacionesAbonados: [
                    { value: 1, text: 'En todas las zonas disponibles' },
                    { value: 2, text: 'Solo en la misma sección que su abono' },
                    { value: 3, text: 'Solo en la misma zona que su abono' }
                ],
                zonasOriginales: [
                    { id: 1, nombre: 'Zona Norte', online: false, taquilla: false },
                    { id: 2, nombre: 'Zona Sur', online: false, taquilla: false },
                    { id: 3, nombre: 'Zona VIP', online: false, taquilla: false }
                ]
            }
        },
        methods: {
            syncChange(event, field, zona) {
                const value = event.target.checked;

                if (this.marcar_en_todos) {
                    this.zonas.forEach(z => {
                        if (!z.invitaciones_abonado) {
                            this.$set(z, 'invitaciones_abonado', { online: false, taquilla: false });
                        }
                        this.$set(z.invitaciones_abonado, field, value);
                    });
                } else {
                    if (!zona.invitaciones_abonado) {
                        this.$set(zona, 'invitaciones_abonado', { online: false, taquilla: false });
                    }
                    this.$set(zona.invitaciones_abonado, field, value);
                }
            },
            resetData() {
                this.errors = [];

                if (this.evento.tipo_invitaciones_abonados?.value !== 1) {
                    this.marcar_en_todos = false;
                    this.zonas = this.zonasOriginales.map(z => ({ ...z }));
                }
            },
            getDetalleEvento() {
                this.loading = true;

                const params = {
                    filters: {},
                    evento_id: this.evento_id
                }

                this.$store.dispatch(this.config.getVuexAlias('back.events.detalle-invitaciones-abonados'), params).then(res => {
                    this.evento = res.data;
                    this.evento.tipo_invitaciones_abonados = this.opcionesTipoInvitacionesAbonados.find(t => t.value == this.evento.tipo_invitaciones_abonados);
                
                    if (this.evento.tipo_invitaciones_abonados === 1) {
                        this.getZonas();
                    }
                }).finally(() => this.loading = false);
            },
            getZonas() {
                this.loading_zonas = true;

                const params = {
                    filters: {
                        search: this.search
                    },
                    evento_id: this.evento_id
                }

                this.$store.dispatch(this.config.getVuexAlias('back.events.zonas-invitaciones-abonados'), params).then(res => {
                    const zonasFromServer = (res.data.data || []).map(z => ({
                        ...z,
                        invitaciones_abonado: {
                            online: !!(z.invitaciones_abonado && z.invitaciones_abonado.online),
                            taquilla: !!(z.invitaciones_abonado && z.invitaciones_abonado.taquilla)
                        }
                    }));

                    this.zonasOriginales = zonasFromServer.map(z => ({
                        ...z,
                        invitaciones_abonado: { ...z.invitaciones_abonado }
                    }));

                    if (this.zonas.length > 0) {
                        this.zonas = zonasFromServer.map(zonaServer => {
                            const zonaExistente = this.zonas.find(z => z.id === zonaServer.id);
                            if (zonaExistente) {
                                return {
                                    ...zonaServer,
                                    invitaciones_abonado: { ...zonaExistente.invitaciones_abonado }
                                };
                            } else {
                                return {
                                    ...zonaServer,
                                    invitaciones_abonado: { ...zonaServer.invitaciones_abonado }
                                };
                            }
                        });
                    } else {
                        this.zonas = zonasFromServer.map(z => ({
                            ...z,
                            invitaciones_abonado: { ...z.invitaciones_abonado }
                        }));
                    }
                }).finally(() => this.loading_zonas = false);
            },
            guardarConfiguracion() {
                this.loading = true;

                const configuracion = this.zonas
                    .filter(z => {
                        const original = this.zonasOriginales.find(o => o.id === z.id);
                        return (
                            z.invitaciones_abonado.online !== original.invitaciones_abonado.online ||
                            z.invitaciones_abonado.taquilla !== original.invitaciones_abonado.taquilla
                        );
                    })
                    .map(z => ({
                        zona_id: z.id,
                        online: z.invitaciones_abonado.online === true,
                        taquilla: z.invitaciones_abonado.taquilla === true
                    }));

                const params = {
                    evento_id: this.evento_id,
                    n_invitaciones_abonados: this.evento.n_invitaciones_abonados,
                    tipo_invitaciones_abonados: this.evento.tipo_invitaciones_abonados.value,
                    configuracion
                };

                this.$store.dispatch(this.config.getVuexAlias('back.events.configuracion-invitaciones-abonados'), params).then(res => {
                    this.$toast.success(res.data?.message);
                    this.zonasOriginales = this.zonas.map(z => ({
                        ...z,
                        invitaciones_abonado: { ...z.invitaciones_abonado }
                    }));
                }).catch(error => {
                    this.errors = [];
                    if (error.response?.data?.errors?.configuracion) {
                        this.errors = [ ...error.response.data.errors.configuracion ];
                    }
                    this.$toast.error(error.response?.data?.message ?? 'Ha ocurrido un error inesperado');
                }).finally(() => this.loading = false);
            },
            async submit() {
                const isValid = await this.$refs.observer.validate();
                if (isValid) {
                    this.guardarConfiguracion();
                }
            }
        },
        computed: {
            altura() {
                return window.innerHeight;
            },
            esMovil() {
                return window.innerWidth <= 768;
            },
            teatro() {
                return this.$store.getters.getTeatro;
            },
            puede_guardar() {
                if (!this.evento.n_invitaciones_abonados || this.evento.n_invitaciones_abonados < 1) return false;
                if (!this.evento.tipo_invitaciones_abonados) return false;

                if (this.evento.tipo_invitaciones_abonados.value === 1) {
                    return this.zonas.some(z => z.invitaciones_abonado.online !== this.zonasOriginales.find(o => o.id === z.id).invitaciones_abonado.online || z.invitaciones_abonado.taquilla !== this.zonasOriginales.find(o => o.id === z.id).invitaciones_abonado.taquilla);
                }

                return true;
            }
        },
        watch: {
            search() {
                this.debouncedGetZonas();
            },
            'evento.tipo_invitaciones_abonados'(newVal) {
                if (newVal.value === 1) this.getZonas();
            }
        },
        created() {
            this.getDetalleEvento();
            this.debouncedGetZonas = _.debounce(this.getZonas, 500)

            if (!this.teatro?.invitaciones_para_abonados) {
                this.$router.go(-1);
            }
        },
        components: { InfoToolTip, MainContent, SearchComponent }
    }
</script>

<style scoped>
.w-responsive {
    width: auto;
}
@media (max-width: 768px) {
    .w-responsive {
        width: 100%;
    }
}
</style>
